import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  DialogTitle,
  IconButton,
  Stepper,
  Step,
  StepButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

const PREFIX = 'OnboardProvider';

const classes = {
  header: `${PREFIX}-header`,
  htop: `${PREFIX}-htop`,
  hbottom: `${PREFIX}-hbottom`,
  title: `${PREFIX}-title`,
  stepper: `${PREFIX}-stepper`,
  steps: `${PREFIX}-steps`,
  stepButton: `${PREFIX}-stepButton`,
  ofstep: `${PREFIX}-ofstep`,
};

const Root = styled(DialogTitle)(({ theme }) => ({
  [`&.${classes.header}`]: {
    backgroundColor: 'rgba(1, 16, 18, 0.04)',
    padding: 0,
  },

  [`& .${classes.htop}`]: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'end',
    height: 50,
  },

  [`& .${classes.hbottom}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginLeft: 144,
    [theme.breakpoints.up('md')]: {
      marginRight: 144,
    },
    [theme.breakpoints.down('md')]: {
      marginRight: 32,
    },
    marginBottom: 16,
    marginTop: -12,
    fontFamily: 'Bitter',
  },

  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
  },

  [`& .${classes.stepper}`]: {
    backgroundColor: '#fff',
    borderRadius: 100,
  },

  [`& .${classes.steps}`]: {
    display: 'flex',
    alignSelf: 'center',
  },

  [`& .${classes.stepButton}`]: {
    width: 120,
  },

  [`& .${classes.ofstep}`]: {
    fontWeight: 'normal',
  },
}));

interface RegisterHeaderProps {
  steps: string[];
  activeStep: number;
  completed: { [k: number]: boolean };
  step: (step: number) => () => void;
  close: (saved: boolean) => void;
  hideCloseButton?: boolean | false;
  isLastStepDisabled: boolean;
  isSaving: boolean;
}

export default function RegisterHeader({
  steps,
  activeStep,
  completed,
  hideCloseButton,
  step,
  close,
  isLastStepDisabled,
  isSaving,
}: RegisterHeaderProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('md'));

  const handleClose = () => {
    close(false);
  };

  return (
    <Root className={classes.header}>
      <Box className={classes.htop}>
        {!hideCloseButton && (
          <IconButton
            onClick={handleClose}
            disabled={isSaving}
            aria-label="close"
            data-testid="close"
            size="large">
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      <Box className={classes.hbottom}>
        <Typography variant="h4" className={classes.title}>
          {t('onboard.register')}
        </Typography>
        {isLarge && (
          <Stepper
            nonLinear
            activeStep={activeStep}
            className={classes.stepper}>
            {steps.map((label, index) => (
              <Step key={label} completed={completed[index]}>
                <StepButton
                  color="inherit"
                  onClick={step(index)}
                  disabled={
                    index < steps.length - 1 !== isLastStepDisabled ||
                    (index > 0 && !completed[index - 1])
                  }>
                  <div className={classes.stepButton}>{label}</div>
                </StepButton>
              </Step>
            ))}
          </Stepper>
        )}
        {!isLarge && (
          <Box className={classes.steps}>
            <Typography variant="subtitle2">
              {t('onboard.curstep', { step: activeStep + 1 })}
            </Typography>
            <Typography variant="subtitle2" className={classes.ofstep}>
              {t('onboard.ofstep', { steps: steps.length })}
            </Typography>
          </Box>
        )}
      </Box>
    </Root>
  );
}
